/**
 * Wallet endpoints of the November 2026 redesign (docs/redesign-2026-11/README.md, Backend contract › Rewards),
 * hand-written next to the generated types until the platform's OpenAPI document carries them:
 *
 * - `GET /v1/me/rewards/weeks?limit=12|52` and `GET /v1/me/rewards/weeks/{epoch_id}`: the weekly chart and statements.
 * - `GET /v1/me/rewards` gains `withdrawn_rao` and `earned_rao`.
 * - `POST /v1/me/withdrawals {amount_rao?}`: a partial withdrawal (422 `below_minimum`, 422 `over_balance`).
 *
 * Amounts are integer rao (1 α = 10^9 rao).
 */
import type { ApiClient } from './client.ts'
import type { MyRewards, Withdrawal } from './types.ts'

const enc = encodeURIComponent

/** counting: the week is still running (`reward_rao` is an estimate); counted: measured, not yet added; paid: added to the balance. */
export type RewardWeekState = 'counting' | 'counted' | 'paid'

/** Points per kind of work: `sim` is robot control, `ego` is recording, `other` kinds not shown by name (scanning). */
export interface PointsByKind {
  sim: number
  ego: number
  other?: number
}

export interface RewardWeek {
  epoch_id: string
  /** ISO week number. */
  week?: number
  starts_at: string
  ends_at: string
  state: RewardWeekState | string
  /** Your (weighted) points that passed checking in this week. */
  points: number
  points_by_kind: PointsByKind
  everyone_points: number
  /** Your share of the week's points, basis points (292 = 2.92%). */
  share_bps: number
  /** What the week adds to your balance; estimated while counting (and counted without a proposal); null without a budget. */
  reward_rao: number | null
  /** The week's reward for all contributors; null without a budget. */
  pool_rao: number | null
  pool_basis?: 'measured' | 'estimate' | null
  /** True while `reward_rao` is an estimate. */
  estimated?: boolean
}

/** One kind of work in a statement, as a newer platform writes it: the server's own label (resilience.md §3). */
export interface StatementItem {
  /** Missing in the map shape (the key is the kind). */
  kind?: string
  label?: string | null
  count?: number | null
  items?: number | null
  points?: number | null
}

/** `GET /v1/me/rewards/weeks/{epoch_id}`: one week plus the pieces of work counted and the week's automatic payout.
 * Read the kinds through `statementKinds` (the server's labelled `kinds` when present, else `items_by_kind`). */
export interface RewardWeekStatement extends RewardWeek {
  items_by_kind?: PointsByKind | null
  /** The same with server-written labels ("Robot control", "Recording", "Other work"): shown when present. */
  kinds?: { key: string; label: string; items: number; points?: number | null }[] | null
  transfer?: { status: 'counted' | 'sent' | string; amount_rao: number; receipt: { extrinsic: string; block?: number | null; at?: string | null; url: string } | null } | null
}

export interface RewardWeeks {
  /** This week first. */
  weeks: RewardWeek[]
}

/** `GET /v1/me/rewards` with the redesign's totals. */
export interface MyRewardsTotals extends MyRewards {
  /** Sent to your wallet so far: withdrawals plus automatic payouts (sent or in transfer). Also inside `balance`. */
  withdrawn_rao?: number
  /** Earned in total, all time (balance + withdrawn). Also inside `balance`. */
  earned_rao?: number
  balance?: MyRewards['balance'] & { withdrawn_rao?: number; earned_rao?: number }
}

export interface WithdrawBody {
  /** Missing (and no `share_bps`) = the whole withdrawable balance. */
  amount_rao?: number
  /** A withdraw shortcut instead of an amount: one of the app config's `rules.withdraw_shortcuts_bps`. */
  share_bps?: number
}

/** Problem codes `POST /v1/me/withdrawals` answers with: 422 below_minimum (+ minimum_rao), 422 over_balance (+ withdrawable_rao), 409 for the rest. */
export const WITHDRAW_PROBLEMS = ['below_minimum', 'over_balance', 'wallet_cooldown', 'no_wallet', 'on_hold', 'ledger_broken'] as const

/** A statement's kinds of work in one shape, `{kind, label (server's, else null), count, points}`, in the order sent:
 * the server's `kinds` when present, else `items_by_kind`. */
export function statementKinds(w: Pick<RewardWeekStatement, 'items_by_kind' | 'kinds'>): { kind: string; label: string | null; count: number; points: number | null }[] {
  if (Array.isArray(w.kinds) && w.kinds.length)
    return w.kinds
      .filter((k) => k && typeof k.key === 'string')
      .map((k) => ({ kind: k.key, label: typeof k.label === 'string' && k.label.trim() ? k.label : null, count: typeof k.items === 'number' ? k.items : 0, points: typeof k.points === 'number' ? k.points : null }))
  return statementItems(w.items_by_kind)
}

/** `items_by_kind` in one shape (today's counts, or labelled kinds). */
export function statementItems(x: PointsByKind | StatementItem[] | Record<string, number | StatementItem> | null | undefined): { kind: string; label: string | null; count: number; points: number | null }[] {
  if (!x) return []
  const one = (kind: string, v: unknown) => {
    if (typeof v === 'number') return { kind, label: null, count: v, points: null }
    if (v && typeof v === 'object') {
      const o = v as StatementItem
      const count = typeof o.count === 'number' ? o.count : typeof o.items === 'number' ? o.items : 0
      return { kind: String(o.kind ?? kind), label: typeof o.label === 'string' && o.label.trim() ? o.label : null, count, points: typeof o.points === 'number' ? o.points : null }
    }
    return null
  }
  const rows = Array.isArray(x) ? x.map((v) => one(String((v as StatementItem)?.kind ?? ''), v)) : Object.entries(x).map(([k, v]) => one(k, v))
  return rows.filter((r): r is NonNullable<typeof r> => r !== null && r.kind !== '')
}

export function walletApi(c: ApiClient) {
  return {
    rewards: () => c.get<MyRewardsTotals>('platform', '/v1/me/rewards'),
    weeks: (limit: number = 12) => c.get<RewardWeeks>('platform', '/v1/me/rewards/weeks', { query: { limit } }),
    week: (epochId: string) => c.get<RewardWeekStatement>('platform', `/v1/me/rewards/weeks/${enc(epochId)}`),
    /** A withdrawal of `amount_rao` (or everything withdrawable) to the linked wallet; phone sessions are allowed too. */
    withdraw: (body: WithdrawBody = {}) => c.post<Withdrawal>('platform', '/v1/me/withdrawals', body),
  }
}

export type WalletApi = ReturnType<typeof walletApi>
